/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 *
 * THE BOARD AS THE CLIENT'S ALEX MAY READ IT: AN ALLOWLIST OF STRUCTURED FACTS
 * (owner decision 22/10/2026, B2 option 1).
 *
 * In documents first mode Alex's prompt carried the analysis board's free text
 * (narratives, the business description, the group structure, the wording of
 * the outstanding documents, the regulator field, names with statuses). Three
 * independent reviews showed a denylist of board words cannot close that. So
 * Alex now gets NO board free text at all. What it gets from the board is built
 * here, from structured facts only:
 *   - names, from the people fields only, cut at the first comma, en or em
 *     dash, spaced hyphen, bracket, semicolon, colon or slash, trimmed (a
 *     hyphen inside a word is part of the name, unless what follows it is a
 *     status or office word); never dropped (a
 *     person stays on the list so their identity email is collected): a cut
 *     that leaves nothing name shaped becomes PERSON_PLACEHOLDER, counted;
 *   - the regulator, only from the hotfix's accepted list (holdRegulatorName,
 *     src/shared/settled-points.js, identical to production 85355f7), in the
 *     list's own wording, never the board's string;
 *   - the company number, the licence or registration number (letters and
 *     digits only, shape checked), the incorporation date (date shaped), the
 *     registered address (address shaped, cut at the first bracket, semicolon
 *     or spaced dash), the jurisdiction (from a fixed list of countries);
 *   - the permissions on the regulator record and the entity type, as fixed
 *     labels from a vocabulary;
 *   - every outstanding or received document, as a fixed document label from a
 *     vocabulary (DOCUMENT_LABELS), never the board's wording; a person
 *     specific item adds the cut name of a person ON the people list only;
 *   - the business, from the client's own intake answer, never the board's
 *     business description;
 *   - each point to cover (documentsFirstQuestions) rebuilt from the label or
 *     topic it maps to, or the platform's own fixed wording; a point that maps
 *     to none is left out and counted (the firm's request lines and the
 *     checklist still carry it).
 * Every value withheld is counted in one log line per build (no silent no op).
 * The board rule (the denylist, client-allowlist.js) is only a second backstop
 * on names and addresses here; nothing depends on it.
 *
 * Pure apart from the count log; no I/O. Nothing it writes carries a dash
 * (standing rule 6).
 */
import { holdRegulatorName } from './settled-points.js';
import { ACTIVITIES } from './permission-gaps.js';
import { boardWordsIn } from './client-allowlist.js';

const str = (v) => (typeof v === 'string' ? v : (typeof v === 'number' && Number.isFinite(v) ? String(v) : ''));
const squash = (s) => String(s).replace(/\s+/g, ' ').trim();
// Second review N1 (22/10/2026): every value is NFKC normalised first, so a full
// width or compatibility form ("ＰＥＰ") reads as its plain letters.
const nfkc = (s) => { try { return String(s).normalize('NFKC'); } catch (_) { return String(s); } };
const uniq = (xs) => [...new Set(xs)];
const DASHES = '\\u2010-\\u2015\\u2212\\-';

// ── The count log ───────────────────────────────────────────────────────────
// One line per build, counts only (never the withheld text).
export function logWithheld(where, counts) {
  if (FULL) return;
  const parts = Object.entries(counts || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`);
  if (parts.length) console.info(`[board-allowlist] ${where}: withheld from Alex ${parts.join(', ')}`);
}
const tally = () => ({ names: 0, regulator: 0, address: 0, numbers: 0, date: 0, jurisdiction: 0, company: 0, permissions: 0, documents: 0, points: 0, entityType: 0 });

// ── Cutting a value at its first separator ──────────────────────────────────
// A full stop ends a sentence unless it closes an initial ("J. Smith") or a
// common abbreviation ("Mr.", "St.").
function cutAtSentence(s) {
  const re = /[.!?](?=\s|$)/g;
  let m;
  while ((m = re.exec(s))) {
    const before = (s.slice(0, m.index).split(/\s+/).pop() || '');
    if (s[m.index] === '.' && (/^\p{L}$/u.test(before) || /^(?:mr|mrs|ms|dr|st|jr|sr|prof|mt|no|ste|apt|rd|ave)$/i.test(before))) continue;
    return s.slice(0, m.index);
  }
  return s;
}
// A name is cut at: a comma; an en dash, em dash or other long dash anywhere; a
// hyphen with a space on either side (" - ", " -", "- ") or not between two
// letters; a bracket; a semicolon; a colon; a slash (and | < > ? !). A hyphen
// INSIDE a word is part of a name ("Jean-Pierre Dupont", "Anne-Marie O'Neill",
// "Ana Smith-Jones") and stays; cutPersonName and cutCompanyName then cut at an
// in word hyphen whose next part is a status or office word ("Ivan
// Petrov-sanctioned"). Apostrophes stay.
const HYPHENS = '\\u2010\\u2011\\-';
const NAME_CUT = new RegExp(`[,;:()\\[\\]{}/|<>?!\\u2012-\\u2015\\u2212]|\\s[${HYPHENS}]|[${HYPHENS}]\\s|(?<!\\p{L})[${HYPHENS}]|[${HYPHENS}](?!\\p{L})`, 'u');
/** The value up to its first comma, long dash, spaced or loose hyphen, bracket, semicolon, colon, slash or sentence end (a hyphen inside a word stays, as an ASCII hyphen). */
export function cutAtSeparator(raw) {
  const s = squash(nfkc(str(raw))).replace(/[\u2010\u2011]/g, '-');
  const i = s.search(NAME_CUT);
  return cutAtSentence(i >= 0 ? s.slice(0, i) : s).replace(/[\s.'’]+$/, '').trim();
}

// ── Names ───────────────────────────────────────────────────────────────────
export const PERSON_PLACEHOLDER = 'a person named on the file';
const PARTICLE = /^(?:of|and|the|de|du|des|da|di|del|della|der|den|van|von|la|le|al|el|bin|bint|ibn|y|e|et|zu|ter|ten|dos|das|do|af|av)$/;
// Third review: in a person's name "of", "and", "the" and "y" are not particles
// ("Ivan of the Cartel", "Pablo the Narco"); "do", "van der", "von" still are.
// (any case: "Al Thani", "Bin Rashid", "Van Dyke")
const PERSON_PARTICLE = /^(?:de|du|des|da|di|del|della|der|den|van|von|la|le|al|el|bin|bint|ibn|e|et|zu|ter|ten|dos|das|do|af|av)$/i;
// Third and fourth reviews: honorifics, titles and offices are not part of a
// name ("Mr Ivan Petrov", "His Excellency Ahmed Khan", "Rt Hon John Smith MP",
// "Tan Sri Ahmad Khan", "Lt Col John Smith" are the name after the title). They
// are stripped from the front in a loop (multi word titles first), and post
// nominals from the end. If nothing name shaped is left, the person is shown by
// role (rolePlaceholder).
const TITLE_PHRASES = [
  'his excellency', 'her excellency', 'his royal highness', 'her royal highness', 'his highness', 'her highness', 'rt hon', 'right hon', 'right honourable', 'right honorable', 'the hon', 'the honourable', 'the honorable', 'the rt hon', 'the right honourable',
  'lt col', 'lieutenant colonel', 'lt gen', 'lieutenant general', 'maj gen', 'major general', 'brig gen', 'rear admiral', 'vice admiral', 'air marshal', 'wing commander',
  'tan sri', 'puan sri', 'dato sri', 'datuk seri', 'dato seri', 'toh puan', 'vice president', 'deputy minister', 'prime minister', 'deputy prime minister', 'chief executive', 'chief executive officer', 'deputy governor', 'vice chairman',
  'mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'sir', 'dame', 'prof', 'professor', 'lord', 'lady', 'herr', 'frau', 'mme', 'mlle', 'he', 'excellency', 'hrh',
  'sheikh', 'sheikha', 'shaikh', 'shaikha', 'sheik', 'hon', 'honourable', 'honorable',
  'gen', 'general', 'col', 'colonel', 'lt', 'lieutenant', 'brig', 'brigadier', 'major', 'maj', 'captain', 'capt', 'admiral', 'adm', 'commander', 'cmdr', 'sgt', 'sergeant', 'marshal', 'field',
  'judge', 'justice', 'prince', 'princess', 'king', 'queen', 'emir', 'emira', 'amir', 'baron', 'baroness', 'count', 'countess', 'duke', 'duchess', 'earl', 'viscount', 'marquess', 'sultan',
  'rev', 'revd', 'reverend', 'father', 'fr', 'imam', 'rabbi', 'pastor', 'bishop', 'archbishop', 'cardinal', 'chief', 'eng', 'engr', 'haji', 'hajji', 'alhaji', 'hajjah', 'hajja',
  // Fourth review recheck: more realistic titles ("HH Sheikh ...", "Crown Prince ...",
  // air force ranks, "Shri", "Hajj", religious titles, "Maitre", "Dipl Ing").
  'crown prince', 'air vice marshal', 'air chief marshal', 'group captain', 'squadron leader', 'flight lieutenant', 'al haj', 'al hajj', 'dipl ing',
  'hh', 'shri', 'shrimati', 'smt', 'hajj', 'alhaj', 'alhajj', 'mufti', 'mullah', 'ayatollah', 'ustaz', 'ustadh', 'comrade', 'maitre', 'maître', 'ing', 'mag',
  'dato', 'datuk', 'datin', 'tun', 'tunku', 'tengku', 'raja', 'president', 'minister', 'ambassador', 'governor', 'mayor', 'senator', 'deputy', 'councillor', 'councilor', 'chairman', 'chairwoman', 'chairperson', 'the',
].map(t => t.split(' '));
// Words that are titles and also common first names ("Amir Khan", "Sultan Al Jaber",
// "Raja Kumar", "Earl Thomas", "Father Brown"): stripped only when at least two name
// words (particles aside) remain after them.
const NAME_LIKE_TITLE = new Set(['amir', 'sultan', 'raja', 'earl', 'father']);
const POST_NOMINAL = /^(?:mp|mep|msp|ms|qc|kc|obe|mbe|cbe|kbe|dbe|gbe|cmg|kcmg|phd|dphil|md|esq|jr|sr|ii|iii|iv|frs|fca|aca|cpa)$/i;
const tkey = (w) => w.toLowerCase().replace(/[.'’]/g, '');
/** The name words with leading titles and trailing post nominals taken off ("H.E." and "HE" are a title only in capitals or with stops). */
export function stripTitles(words) {
  let w = words.slice();
  for (let guard = 0; guard < 8 && w.length; guard++) {
    const hit = TITLE_PHRASES.find(ph => ph.length <= w.length && ph.every((t, i) => tkey(w[i]) === t) && (ph.join(' ') !== 'he' || /^(?:HE|H\.E\.?)$/.test(w[0])));
    if (!hit) {
      if (w[0] === 'M.' && w.length > 2) { w = w.slice(1); continue; }
      break;
    }
    if (hit.length === 1 && NAME_LIKE_TITLE.has(hit[0]) && w.slice(1).filter(x => !PERSON_PARTICLE.test(tkey(x))).length < 2) break;
    w = w.slice(hit.length);
  }
  while (w.length > 1 && POST_NOMINAL.test(tkey(w[w.length - 1]))) w = w.slice(0, -1);
  return w;
}
// Third review N6 (product owner): the officer report keeps the full recorded
// name. withFullNames runs a build with every client facing cut switched off.
let FULL = false;
export function withFullNames(fn) { const prev = FULL; FULL = true; try { return fn(); } finally { FULL = prev; } }
export const fullNamesOn = () => FULL;
// What "name shaped" means here. A person's name is at least two words (a
// forename and a surname), each starting with a capital (a small particle such
// as "van" or "de" aside), at most eight words. The cut alone cannot tell a
// name from a status written in capitals with no separator, so the shape test
// also reads three short fixed lists, which are part of the name rule (not the
// board denylist):
//   - a status acronym in capitals ends the name there ("Marisol Brandreth PEP");
//   - a relation, office or status word ends it there ("Ivan Petrov Former
//     Minister", "Harlan Reyes Nephew Of ...");
//   - a word no name starts with means the value is not a name ("Under
//     Investigation", "In Liquidation", "Fraud Suspected", "Rated Medium").
// A real surname that is on a list ("Helen Fraud", "Peter Weak") is kept: only
// the first word and the trailing words are read. Fails safe: a value that is
// not name shaped becomes PERSON_PLACEHOLDER, so the person stays on the list.
const STATUS_ACRONYM = /^(?:PEPs?|SARs?|OFAC|OFSI|HMT|FATF|EDD|MLRO|KYC|AML|CDD|UBOs?|RCA|SDN|SPA|POA|PSC|SoW|SoF|FIU|SFO|NCA|RAG)$/;
const NAME_TRAILER = /^(?:nephew|niece|cousin|son|daughter|brother|sister|wife|husband|spouse|relative|associate|associates|former|deputy|minister|secretary|governor|senator|ambassador|politician|sanctioned|designated|listed|politically|exposed|suspected|alleged|allegedly|convicted|fugitive|interpol|panama|pandora|dormant|struck|dissolved|insolvent|bankrupt|disqualified|unknown|unverified|unconfirmed|unsigned|forged|fake)$/i;
const STATUS_START = /^(?:deceased|late|ex|former|under|not|no|non|in|on|at|by|for|with|without|possible|possibly|potential|pending|high|higher|low|lower|medium|moderate|elevated|enhanced|risk|risky|alleged|suspected|criminal|fraud|fraudulent|fake|forged|struck|dissolved|liquidation|sanctioned|sanctions|blacklisted|flagged|mismatch|discrepancy|inconsistent|insufficient|weak|satisfactory|unsatisfactory|unverified|unconfirmed|unknown|cancelled|canceled|lapsed|suspended|revoked|expired|refer|referred|decline|declined|reject|rejected|approve|approved|approval|recommend|recommended|rated|scored|score|scoring|grade|graded|rating|requires|required|partially|partly|adequately|plausible|implausible|credible|signed|unsigned|officer|board|mlro|ask|request|obtain|please|do|avoid|hold|negative|adverse|media|listed|evasive|outside|second|compliance|explanation|source|deposits?|director|company|business|customers?|funds?|transactions?|ownership|identity|kyc|customer|wealth|story|turnover|large|monthly|licence|license|activity|unexplained|unusual|atypical|shell|nominee|dormant|virtual|offshore|layering|structuring|evasion|bribery|corruption|terrorist|terrorism|laundering|proceeds|politically|exposed|nephew|niece|relative|associate|interpol|panama|ofsi|treasury|asset|eu|hmt|uk|consolidated|restrictive|freeze|designation|outstanding|missing|unable|incomplete|verification|verify|verified)$/i;
// Words the board rule (the backstop below) must not read as a status when they open a real name.
const NAME_SAFE = /^(?:amber|edd|rag|hits?|thin|established|outcome|confidence|lack|pep)$/i;
const LEGAL_SUFFIX = /\b(?:ltd|limited|plc|llp|llc|lp|inc|incorporated|corp|corporation|gmbh|ag|sa|sarl|sas|bv|nv|oy|ab|aps|as|kft|spa|srl|se|kg|pte|pty|holdings?|group|trust|foundation|partners|company|co)\b\.?$/i;
// A word of a name: each hyphen joined part starts with a capital (a small
// particle such as "de" may follow a hyphen: "Jean-de-Dieu").
const namePart = (seg) => /^[\p{Lu}][\p{L}'’.]*$/u.test(seg);
const companyPart = (seg) => /^[\p{Lu}\d&][\p{L}\d'’.&]*$/u.test(seg);
const hyphenWord = (w, part) => w.split('-').every((seg, j) => part(seg) || (j > 0 && PARTICLE.test(seg)));
const personToken = (w, i) => hyphenWord(w, namePart) || (i > 0 && PERSON_PARTICLE.test(w));
const companyToken = (w, i) => hyphenWord(w, companyPart) || (i > 0 && PARTICLE.test(w));
const statusWord = (w) => STATUS_ACRONYM.test(w) || NAME_TRAILER.test(w) || STATUS_START.test(w);
// An in word hyphen whose next part is a status or office word from the lists
// above (or a lower case word that is not a particle) ends the name there:
// "Ivan Petrov-sanctioned" is "Ivan Petrov", "Kestrel Pay Ltd-dormant" is
// "Kestrel Pay Ltd". "Jean-Pierre", "Smith-Jones", "Jean-de-Dieu" stay.
function cutAtStatusHyphen(tokens) {
  for (let i = 0; i < tokens.length; i++) {
    const segs = tokens[i].split('-');
    const at = segs.findIndex((seg, j) => j > 0 && (statusWord(seg) || (!/^[\p{Lu}\d]/u.test(seg) && !PARTICLE.test(seg))));
    if (at > 0) return [...tokens.slice(0, i), segs.slice(0, at).join('-')];
  }
  return tokens;
}
// A status, office, relation or risk word AFTER a complete name ends it, in
// any case ("John Smith Under Investigation", "Omar Haddad Fraudster",
// "Kestrel Pay Ltd In Liquidation", "Ahmed Khan pep family member"). Part of
// the name rule, not the board denylist. A surname alone is never read here
// ("Helen Fraud" stays a person): a person's name is complete after two words,
// a company's after its legal form (Ltd, AG and the rest), else after two words
// and then only at a plainly status word (STATUS_AFTER_COMPANY), so a business
// word in a trading name ("Global Risk Partners") is not cut.
const STATUS_ACRONYM_CI = new RegExp(STATUS_ACRONYM.source, 'i');
const RISK_WORD = /^(?:cartels?|mafia|mafioso|mob|narcos?|narcotics|cocaine|heroin|drugs?|gangsters?|gangs?|kingpin|boss|terrorists?|qaeda|isis|hamas|hezbollah|shabaab|taliban|fraudsters?|scam|scammers?|kremlin|oligarchs?|crony|cronies|linked|connected|arms|dealers?|traffickers?|trafficking|smugglers?|smuggling|launderers?|watchlist|watchlisted|match|matched|resigned|removed|dismissed|fired|probe|probed|investigation|investigations|investigated|inquiry|enquiry|target|targeted|family|member|members|nominees?|proxy|straw|puppet|shell|clone|cloned|front|formerly|previously|aka|alias|known|liquidator|administration|receivership|insolvency|phoenix|accommodation|mail|maildrop|formation|agent|agents|secrecy|enforcement|charged|indicted|arrested|wanted|jailed|imprisoned|terror|sanctions?|adverse|watch|hits?|flag|flags|warning|unlicensed|unlicenced|unregistered|unauthori[sz]ed|blocked|frozen|seized|debarred|banned|barred|deregistered|delisted|struck|dissolved|insolvent|liquidation|liquidated|bankrupt|bankruptcy|dormant|defunct|ceased|rated|risky|risk|pep|peps)$/i;
const STATUS_AFTER_COMPANY = /^(?:under|liquidation|liquidated|liquidator|administration|receivership|insolvency|insolvent|bankrupt|bankruptcy|formerly|previously|aka|alias|fraud|fraudulent|fraudsters?|scam|clone|cloned|shell|phoenix|sanctioned|sanctions?|investigation|investigated|enforcement|struck|dissolved|deregistered|delisted|defunct|ceased|blacklisted|flagged|watchlist|watchlisted|suspected|alleged|allegedly|adverse|kremlin|nominees?|front|revoked|cancelled|canceled|suspended|unlicensed|unlicenced|unregistered|unauthori[sz]ed|debarred|banned|frozen|seized|dormant)$/i;
// Words that join a status phrase to the name ("In Liquidation", "Under FCA
// Investigation", "Formerly Known As"): the cut moves back over them.
const STATUS_JOIN = /^(?:in|under|not|no|now|formerly|previously|known|as|also|a|an|the|of|with|by|for|on|at|possible|possibly|potential|pending|suspected|alleged|allegedly|after|before|subject|to|being|is|was|has|been|high|higher|very)$/i;
const LEGAL_WORD = /^(?:ltd|limited|plc|llp|llc|lp|inc|incorporated|corp|corporation|gmbh|ag|sa|sarl|sas|bv|nv|oy|ab|aps|kft|srl|se|kg|pte|pty)\.?$/i;
const broadStatus = (w) => !PARTICLE.test(w) && (STATUS_ACRONYM_CI.test(w) || NAME_TRAILER.test(w) || STATUS_START.test(w) || RISK_WORD.test(w));
function cutAfterName(tokens, kind) {
  const plain = (w) => w.replace(/[.'’]+$/, '');
  const legalAt = kind === 'company' ? tokens.findIndex((w, i) => i > 0 && LEGAL_WORD.test(plain(w))) : -1;
  let at = -1;
  for (let i = 1; i < tokens.length; i++) {
    const w = plain(tokens[i]);
    if ((kind === 'person' && i >= 2 && broadStatus(w))
      || (kind === 'company' && legalAt >= 0 && i > legalAt && broadStatus(w))
      || (kind === 'company' && i >= 2 && STATUS_AFTER_COMPANY.test(w))) { at = i; break; }
  }
  if (at < 0) return tokens;
  const floor = kind === 'company' && legalAt >= 0 && at > legalAt ? legalAt + 1 : (kind === 'person' ? 2 : 1);
  while (at > floor && STATUS_JOIN.test(plain(tokens[at - 1]))) at--;
  return tokens.slice(0, at);
}
// N1: a token whose letters mix scripts (Latin with Cyrillic or Greek, "Sаnctioned"
// with a Cyrillic a), or a token in another script than the name's first token
// ("John Smith РЕР"), ends the value there. A status word glued to a word or in
// camel case ("SmithPEP", "LtdSanctioned", "SanctionedEntity") is split out and
// ends it too; a real camel case name ("McDonald", "DeVito") is not a status.
const SCRIPTS = [['latin', /\p{Script=Latin}/u], ['cyrillic', /\p{Script=Cyrillic}/u], ['greek', /\p{Script=Greek}/u], ['other', /[^\p{Script=Latin}\p{Script=Cyrillic}\p{Script=Greek}\p{Script=Common}\p{Script=Inherited}]/u]];
const scriptsOf = (w) => SCRIPTS.filter(([, re]) => re.test(w)).map(([k]) => k);
// The status acronyms that are never a name, read in any case ("John Pep", "John Smith Ofac").
const STRONG_ACRONYM_CI = /^(?:peps?|sars?|ofac|ofsi|hmt|fatf|edd|mlro|kyc|aml|cdd|ubos?|sdn|fiu|sfo|nca)$/i;
const gluedSegments = (w) => w.split(/(?<=\p{Ll})(?=\p{Lu})|(?<=\p{Lu})(?=\p{Lu}\p{Ll})/u).filter(Boolean);
function cutAtScriptAndGlue(tokens) {
  const first = tokens.length ? scriptsOf(tokens[0]) : [];
  for (let i = 0; i < tokens.length; i++) {
    const sc = scriptsOf(tokens[i]);
    if (sc.length > 1 || (i > 0 && sc.length === 1 && first.length === 1 && sc[0] !== first[0])) return tokens.slice(0, i);
    // Third review B2: a full stop or apostrophe (straight or curly) splits a token
    // too ("John Smith.PEP", "John Smith'PEP"); a single letter initial ("J.") and an
    // O' or D' prefix stay as they are, since neither is a status word.
    const segs = tokens[i].split(/[-.'’‘]/).filter(Boolean).flatMap(gluedSegments);
    if (segs.length > 1) {
      const j = segs.findIndex(seg => STATUS_ACRONYM.test(seg) || STRONG_ACRONYM_CI.test(seg) || broadStatus(seg));
      if (j === 0) return tokens.slice(0, i);
      if (j > 0) return [...tokens.slice(0, i), segs.slice(0, j).join('')];
    }
  }
  return tokens;
}
function trimAtStatus(tokens0, kind = 'person') {
  const tokens = cutAtStatusHyphen(cutAtScriptAndGlue(tokens0));
  if (tokens.length && STATUS_START.test(tokens[0].split('-')[0])) return [];
  const at = tokens.findIndex((w, i) => w.split('-').some(seg => STATUS_ACRONYM.test(seg) || NAME_TRAILER.test(seg) || (i > 0 && STRONG_ACRONYM_CI.test(seg))));
  return cutAfterName(at < 0 ? tokens : tokens.slice(0, at), kind);
}
// Backstop only (the board rule, client-allowlist.js): a listed phrase anywhere
// or a listed word before the last word fails the name; a listed word as the
// surname ("Helen Fraud", "Peter Weak") never does. Nothing depends on it (the
// harness runs with it switched off).
function backstopFails(name, tokens) {
  const hits = boardWordsIn(name);
  if (!hits.length) return false;
  const lastStart = name.length - tokens[tokens.length - 1].length;
  return hits.some(h => /\s/.test(h.word.trim()) || (h.index < lastStart && !NAME_SAFE.test(h.word.trim())));
}
// Second review B1 (22/10/2026): a structural rule, not a word list. A person's
// name is its first two name words (a particle between them stays: "Jean van
// der Berg", "Maria do Carmo"); a company in a people field is its first two
// words and a legal form straight after them ("Ocean Crest Ltd"). A longer name
// is kept whole only when a second source gives the same name token for token
// (corroborationSet: the identity verification list, the register extracts,
// the same name in another people field, or the client's own contact name).
// Whatever the board wrote after the second word ("Pablo Escobar Narco Boss",
// "John Smith Red Flag") never reaches Alex unless another source wrote it too.
export const nameKey = (s) => nfkc(str(s)).toLowerCase().replace(/[.'’]/g, '').replace(/\s+/g, ' ').trim();
function twoTokenForm(tokens, corporate) {
  const out = [];
  let words = 0;
  const PART = corporate ? PARTICLE : PERSON_PARTICLE;
  for (const w of tokens) {
    if (words >= 2) { if (corporate && LEGAL_WORD.test(w.replace(/[.'’]+$/, ''))) out.push(w); break; }
    out.push(w);
    if (!PART.test(w)) words++;
  }
  return out;
}
/** A person's name from a people field, or '' when nothing name shaped is left. */
export function cutPersonName(raw, { corporate = false, corroborated = null } = {}) {
  if (FULL) return squash(str(raw));
  // Titles come off the raw value first ("Hon. John Smith" would otherwise end at "Hon."), post nominals after the cut.
  const lead = stripTitles(squash(nfkc(str(raw))).split(' ').filter(Boolean)).join(' ');
  let parts = stripTitles(cutAtSeparator(lead).split(' ').filter(Boolean));
  let tokens = trimAtStatus(parts);
  const looksCorporate = corporate || tokens.some((w, i) => i > 0 && LEGAL_WORD.test(w.replace(/[.'’]+$/, '')));
  // Third review: a name with a particle whose last word is a status or risk word
  // ends before the particle ("Ivan de la Mafia", "Ivan van der Narco").
  if (!looksCorporate) {
    const pi = tokens.findIndex((w, i) => i > 0 && PERSON_PARTICLE.test(w));
    const last = (tokens[tokens.length - 1] || '').replace(/[.'’]+$/, '');
    if (pi > 0 && (broadStatus(last) || RISK_WORD.test(last) || STRONG_ACRONYM_CI.test(last))) tokens = tokens.slice(0, pi);
  }
  const PART = looksCorporate ? PARTICLE : PERSON_PARTICLE;
  const words = tokens.filter(w => !PART.test(w)).length;
  if (words > 2 || (looksCorporate && tokens.length > 3)) {
    const whole = corroborated instanceof Set && corroborated.has(nameKey(tokens.join(' ')));
    if (!whole) tokens = twoTokenForm(tokens, looksCorporate);
  }
  const name = tokens.join(' ');
  if (!tokens.length || tokens.length > 8 || name.length > 80 || !/\p{L}/u.test(name)) return '';
  const asPerson = tokens.length >= 2 && tokens.every(personToken) && tokens.filter(w => !PERSON_PARTICLE.test(w)).length >= 2;
  const asCompany = (corporate || LEGAL_SUFFIX.test(name)) && tokens.length >= 2 && tokens.every(companyToken);
  if (!asPerson && !asCompany) return '';
  if (backstopFails(name, tokens)) return '';
  return name;
}
/** A company's name (the client name, a corporate owner), or '' when nothing name shaped is left. */
export function cutCompanyName(raw) {
  const tokens = trimAtStatus(cutAtSeparator(raw).split(' ').filter(Boolean), 'company');
  const name = tokens.join(' ');
  if (!tokens.length || tokens.length > 10 || name.length > 100 || !/\p{L}/u.test(name)) return '';
  if (!tokens.every(companyToken)) return '';
  // One word is a company name only with its legal form ("Kestrel Ltd" is two words).
  if (tokens.length < 2) return '';
  if (boardWordsIn(name).some(h => /\s/.test(h.word.trim()))) return '';   // backstop only
  return name;
}

// The people fields a board can carry, each read for names only.
export const PEOPLE_KEYS = Object.freeze(['directors', 'ubos', 'shareholders', 'authorisedSignatories', 'signatories', 'controllingPersons', 'controllers', 'officers', 'trustees', 'settlors', 'protectors', 'beneficiaries', 'partners']);
const EMAIL = /^[^\s@,;()<>]+@[^\s@,;()<>]+\.[A-Za-z]{2,}$/;
/** A person entry as Alex may read it: the cut name (or the placeholder), a shape checked email, the ownership as a number. */
export function allowedPerson(p, counts, { corroborated = null } = {}) {
  const raw = p && typeof p === 'object' ? p.name : p;
  const corporate = !!(p && typeof p === 'object' && (p.corporateUBO === true || p.holderType === 'entity'));
  let name = cutPersonName(raw, { corporate, corroborated });
  if (!name) { name = PERSON_PLACEHOLDER; if (counts) counts.names++; }
  const out = { name };
  if (p && typeof p === 'object') {
    const email = squash(str(p.email));
    if (EMAIL.test(email)) out.email = email;
    const own = squash(str(p.ownership)).replace(/%$/, '');
    if (/^\d{1,3}(?:\.\d{1,2})?$/.test(own) && Number(own) <= 100) out.ownership = own;
  }
  return out;
}
/** The cut names of everyone on the people fields (no placeholders), for matching person specific items. */
export function peopleNames(board, collected = null) {
  const b = board && typeof board === 'object' ? board : {};
  const names = [];
  const corroborated = corroborationSet(b, collected);
  for (const k of PEOPLE_KEYS) for (const p of (Array.isArray(b[k]) ? b[k] : [])) {
    const n = cutPersonName(p && typeof p === 'object' ? p.name : p, { corporate: !!(p && p.corporateUBO), corroborated });
    if (n) names.push(n);
  }
  return uniq(names);
}

/**
 * Third review B1 (22/10/2026): the full names a source the analysis model did
 * not write gives, as name keys (nameKey). Only the client's own contact name
 * qualifies: collected.contactName (written by the page from the client's own
 * entry, src/App.jsx upload form, or from the invitation an officer typed,
 * api/invite.js) and dfContext.contactName (the client's own upload form). Not
 * board.sumsubRequired (df-process rebuilds it from the board's own people),
 * not the same string in two board people fields (one model wrote both), not
 * the register extracts (df-process takes them from the document reading's
 * registerExtract, which the reading model writes), and no Sumsub applicant name
 * (the Sumsub path stores none: api/_sumsub-outcome.js writes the outcome only,
 * and api/sumsub.js reads Sumsub's name to compare, never stores it).
 */
export function corroborationSet(board, collected = null) {
  void board;
  const cd = collected && typeof collected === 'object' ? collected : {};
  const ctx = cd.dfContext && typeof cd.dfContext === 'object' ? cd.dfContext : {};
  const out = new Set();
  const add = (v) => {
    const parts = stripTitles(cutAtSeparator(stripTitles(squash(nfkc(str(v))).split(' ').filter(Boolean)).join(' ')).split(' ').filter(Boolean));
    if (parts.length) out.add(nameKey(parts.join(' ')));
  };
  add(cd.contactName); add(ctx.contactName);
  return out;
}
// N7: where a person's name is not used, a client facing line names the role.
const ROLE_OF = { directors: 'director', ubos: 'beneficial owner', shareholders: 'shareholder', authorisedSignatories: 'authorised signatory', signatories: 'signatory', controllingPersons: 'controlling person', controllers: 'controlling person', officers: 'officer', trustees: 'trustee', settlors: 'settlor', protectors: 'protector', beneficiaries: 'beneficiary', partners: 'partner' };
/** The client facing form of a person whose name is not used: "the director named on the file". */
export const rolePlaceholder = (key) => (ROLE_OF[key] ? `the ${ROLE_OF[key]} named on the file` : PERSON_PLACEHOLDER);

// ── The company name (second review B1, 22/10/2026) ─────────────────────────
// The board's company name is used only when it matches, after normalisation,
// a name the client typed (collected.companyName, dfContext.companyName,
// prefilled_companyName) or a register's legal name (the pre screen's register
// entry, a register extract's subject). Otherwise the client's own typed name is
// used. No word the client or the register did not supply is ever taken from
// the board.
const companyKey = (s) => nfkc(str(s)).toLowerCase().replace(/&/g, ' and ').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(w => w && !LEGAL_WORD.test(w) && w !== 'the' && w !== 'public' && w !== 'company').join(' ');
const typedName = (v) => { const t = squash(nfkc(str(v))).split(/[\r\n]/)[0]; return t.length <= 100 ? t : ''; };
export function companyNameSources(collected) {
  const cd = collected && typeof collected === 'object' ? collected : {};
  const ctx = cd.dfContext && typeof cd.dfContext === 'object' ? cd.dfContext : {};
  const ps = cd.preScreen && typeof cd.preScreen === 'object' ? cd.preScreen : {};
  // Third review B1: only names the client (or the inviting officer) typed. The
  // pre screen's company name (api/pre-screen.js, the research model's "Legal
  // name if found"), a register extract's subject (the reading model) and the
  // website prefill (the research model) are model written, so none counts.
  void ps;
  const typed = uniq([cd.companyName, ctx.companyName].map(typedName).filter(Boolean));
  return { typed, register: [] };
}
/**
 * The company's name as a client view may carry it: { name, from } where from
 * is 'board' (the board's cut name, matched to a typed or register name),
 * 'client' (the client's own typed name), or '' (no name).
 */
export function trustedCompanyName(board, collected) {
  const b = board && typeof board === 'object' ? board : {};
  const cd = b.companyDetails && typeof b.companyDetails === 'object' ? b.companyDetails : {};
  if (FULL) { const raw = squash(str(b.clientName || cd.legalName)); return raw ? { name: raw, from: 'board' } : { name: '', from: '' }; }
  const { typed, register } = companyNameSources(collected);
  const keys = new Set([...typed, ...register].map(companyKey).filter(Boolean));
  for (const raw of [b.clientName, cd.legalName]) {
    const cut = cutCompanyName(raw);
    if (cut && keys.has(companyKey(cut)) && companyKey(cut) === companyKey(squash(nfkc(str(raw))))) return { name: cut, from: 'board' };
  }
  return typed.length ? { name: typed[0], from: 'client' } : { name: '', from: '' };
}

// ── The regulator ───────────────────────────────────────────────────────────
/** The regulator in the accepted list's own wording (holdRegulatorName), or ''. */
export function allowedRegulator(raw) {
  const v = squash(str(raw));
  return v ? holdRegulatorName(v) : '';
}

// ── Numbers, the date, the address, the jurisdiction ────────────────────────
const VALUE_CUT = new RegExp(`[,;:()\\[\\]{}|<>]|\\s[${DASHES}]+\\s`);
/** A company, licence or registration number: letters and digits only, shape checked. */
// Second review N2 (22/10/2026): a number's letters may only be a registry
// prefix from this fixed list (Companies House SC, OC, NI, NC, SO, R and the
// rest, the FCA's FRN, the Swiss CHE, German HRB and HRA, a FINTRAC M, a
// Mauritius GB, a Cyprus HE, a Malta C, a Luxembourg B), followed by digits and
// at most one trailing check letter. "HIT 12345", "RISK999", "KGB1234" are refused.
const REGISTRY_PREFIX = ['FRN', 'CHE', 'HRB', 'HRA', 'VR', 'PR', 'GNR', 'SC', 'OC', 'NI', 'NC', 'SO', 'SL', 'SE', 'SA', 'SF', 'SG', 'SP', 'SI', 'SZ', 'SR', 'R', 'IP', 'FC', 'LP', 'NF', 'NL', 'NP', 'NR', 'NZ', 'GE', 'GS', 'ES', 'RC', 'CE', 'CS', 'IC', 'ZC', 'M', 'GB', 'HE', 'C', 'B'];
const NUMBER_SHAPE = new RegExp(`^(?:${REGISTRY_PREFIX.join('|')})?\\d{3,18}[A-Z]?$`);
export function allowedNumber(raw) {
  let s = squash(nfkc(str(raw)));
  const i = s.search(VALUE_CUT);
  if (i >= 0) s = s.slice(0, i);
  s = cutAtSentence(s);
  // A number never starts with a status acronym ("SAR 1234", "PEP 2019").
  const lead = (s.trim().match(/^\p{L}+/u) || [''])[0];
  if (lead && STATUS_ACRONYM_CI.test(lead)) return '';
  const compact = s.replace(new RegExp(`[\\s./${DASHES}]`, 'g'), '').toUpperCase();
  if (!/^[A-Z0-9]{3,20}$/.test(compact)) return '';
  if (STATUS_ACRONYM_CI.test((compact.match(/^[A-Z]+/) || [''])[0])) return '';
  return NUMBER_SHAPE.test(compact) ? compact : '';
}
const MONTH = '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
/** The incorporation date, date shaped only (an ISO date is written day/month/year, no dash). */
export function allowedDate(raw) {
  let s = squash(str(raw));
  const i = s.search(/[,;()\[\]{}]|\s[\u2010-\u2015\u2212-]+\s/);
  if (i >= 0 && !new RegExp(`^${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?,\\s*\\d{4}$`, 'i').test(s)) s = s.slice(0, i).trim();
  s = s.replace(/\.$/, '');
  let m;
  if ((m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.Z+]*)?$/))) return `${m[3]}/${m[2]}/${m[1]}`;
  if (/^\d{1,2}[/.]\d{1,2}[/.]\d{2,4}$/.test(s)) return s.replace(/\./g, '/');
  if (new RegExp(`^\\d{1,2}(?:st|nd|rd|th)?\\s+${MONTH}\\s+\\d{4}$`, 'i').test(s)) return s;
  if (new RegExp(`^${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4}$`, 'i').test(s)) return s;
  if (new RegExp(`^${MONTH}\\s+\\d{4}$`, 'i').test(s)) return s;
  if (/^(?:18|19|20)\d{2}$/.test(s)) return s;
  return '';
}
const STREET_WORD = /\b(?:street|st|road|rd|lane|ln|avenue|ave|way|drive|dr|close|square|sq|place|pl|row|court|ct|crescent|terrace|gardens|park|hill|house|building|floor|suite|unit|flat|apartment|strasse|straße|str|gasse|platz|weg|allee|rue|via|viale|calle|avenida|boulevard|blvd|quay|wharf|mews|walk|grove|parade|highway|plaza|centre|center|tower|chambers|box|po|plot|block|estate)\b/i;
const ADDRESS_LOWER = /^(?:of|and|the|de|du|des|da|di|del|della|der|den|van|von|la|le|al|el|rue|via|calle|avenida|strada|piazza|chemin|avenue|boulevard|am|an|im|auf|zum|zur|y|e|st|no)$/i;
// Words a place name may start with although a status may too ("High Wycombe").
const ADDRESS_STATUS_OK = /^(?:high|higher|low|lower|upper)$/i;
// Product owner (22/10/2026): assessment words are never address parts ("Vacant",
// "Unoccupied", "Dormant", "Nominee", "Shell"), and neither are the assessment
// phrases in ADDRESS_PHRASE: a part holding one is cut, as "Liquidation Row" is.
const ADDRESS_RISK = /^(?:accommodation|mail|maildrop|formation|agent|agents|secrecy|risk|risky|virtual|shell|nominee|nominees|vacant|unoccupied|dormant|sanctioned|sanctions|flagged|blacklisted|suspected|suspicious|alleged|fake|fraud|fraudulent|offshore|unverified|unconfirmed|dissolved|struck)$/i;
const ADDRESS_PHRASE = /\b(?:mass\s+registration|registration\s+address|virtual\s+office|registered\s+agent\s+address)\b/i;
const addressStatus = (w) => { const x = w.replace(/[.'’]+$/, ''); return STATUS_ACRONYM.test(x) || /^(?:peps?|sars?|ofac|ofsi|fatf|mlro|sdn|kyc|aml|edd)$/i.test(x) || NAME_TRAILER.test(x) || ADDRESS_RISK.test(x); };
// Second review B1 (22/10/2026): the address is structural, not a word list.
// It keeps the number and street (and a flat, unit, suite or PO box before it),
// then only parts that are a postcode or number, a listed country, or a single
// word place name (once, optionally with its postcode: "London EC2V 8AU",
// "1204 Geneva"). It stops at the first part of any other shape, so no free
// capitalised phrase ("Does Not Exist", "Mass Registration Address", "Kremlin
// Linked") is ever carried. A status word, a mixed script or a glued status
// word in a kept part still ends it.
const STREET_TOKEN = new RegExp('^(?:' + STREET_WORD.source.replace(/^\\b\(\?:/, '').replace(/\)\\b$/, '') + ')\\.?$', 'i');
// (a UK outward code alone, "SE1", "W1K", counts too)
const POSTCODE = /^(?:[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}|[A-Z]{1,2}\d[A-Z\d]?|[A-Z]{1,2}\s?\d{3,6}|\d{3,6}(?:\s\d{3,4})?|\d{1,5}[A-Z]?)$/i;
const PLACE_WORD = /^\p{Lu}[\p{Ll}'’]{1,30}$/u;
const wordOk = (w, i) => /^[\p{Lu}\d#][\p{L}\d'’.#]*$/u.test(w) || ADDRESS_LOWER.test(w) || (i > 0 && /^\d/.test(w));
const badWord = (w) => addressStatus(w);
// A compound street name ("Bahnhofstrasse", "Kalverstraat") ends in its street word.
const STREET_SUFFIX = /(?:strasse|straße|gasse|platz|weg|allee|straat|laan|gracht|gade|vej|katu|utca|ulica)$/i;
const isStreetToken = (w) => STREET_TOKEN.test(w) || (w.length > 6 && STREET_SUFFIX.test(w));
const cleanWords = (part) => cutAtScriptAndGlue(part.split(' ').filter(Boolean));
// The number and street of a part, or null: "1 King Street", "Rue du Rhone 1", "Flat 2", "PO Box 77".
// Fourth review: the whole part is kept when it reads as a number and a street
// ("1 Park Lane", "1 Tower Bridge Road", "45 Avenue Mohamed V", "3 More London
// Riverside"); otherwise it is kept up to its last street word ("1 King Street
// London Mafia Front" is "1 King Street"). A status or risk word never passes.
const riskyAddressWord = (w) => { const x = w.replace(/[.'’]+$/, ''); return badWord(x) || RISK_WORD.test(x) || NAME_TRAILER.test(x) || (STATUS_START.test(x) && !ADDRESS_STATUS_OK.test(x) && !isStreetToken(x)); };
function streetOf(words) {
  if (!words.length || words.length > 10 || !words.every(wordOk)) return null;
  const ni = words.findIndex(w => /\d/.test(w));
  if (ni < 0) return null;
  const isSt = words.map(isStreetToken);
  const si = isSt.indexOf(true), sl = isSt.lastIndexOf(true);
  const clean = !words.some(riskyAddressWord);
  // Whole: ends in its street word or number, or a continental street word straight after the number ("45 Avenue Mohamed V"),
  // or a number and up to three name words with no street word at all ("3 More London Riverside").
  if (clean && ((sl >= 0 && (sl === words.length - 1 || /\d/.test(words[words.length - 1]))) || (si === ni + 1 && words.length - si <= 4) || (si < 0 && ni === 0 && words.length >= 2 && words.length <= 4))) return words.join(' ');
  if (si < 0) return null;
  const kept = ni < sl ? words.slice(0, sl + 1) : words.slice(0, ni + 1);
  return kept.some(riskyAddressWord) ? null : kept.join(' ');
}
// A building name between a flat or unit part and the street ("Dock House") is left out, never carried.
const isBuildingPart = (words) => words.length >= 1 && words.length <= 4 && words.every(wordOk) && !/\d/.test(words.join(' ')) && isStreetToken(words[words.length - 1]);
function placeOf(words, placeTaken) {
  const t = words.join(' ');
  if (!words.length) return null;
  if (allowedCountry(t)) return { text: allowedCountry(t) === t ? t : t, kind: 'country' };
  if (POSTCODE.test(t)) return { text: t, kind: 'postcode' };
  if (placeTaken) return null;
  const isPlace = (w) => PLACE_WORD.test(w) && !badWord(w) && !STATUS_START.test(w) && !NAME_TRAILER.test(w) && !RISK_WORD.test(w);
  if (words.length === 1 && isPlace(words[0])) return { text: t, kind: 'place' };
  // A place with its postcode, either way round.
  for (let k = 1; k < words.length; k++) {
    const a = words.slice(0, k).join(' '), z = words.slice(k).join(' ');
    if (k === 1 && isPlace(words[0]) && POSTCODE.test(z)) return { text: t, kind: 'place' };
    if (k === words.length - 1 && POSTCODE.test(a) && isPlace(words[k])) return { text: t, kind: 'place' };
  }
  return null;
}
/** The registered address, structural only: cut at the first bracket, semicolon, colon, spaced dash or sentence end; then the number and street, then postcode, country and one single word place parts. */
export function allowedAddress(raw) {
  if (FULL) return squash(str(raw));
  let s = squash(nfkc(str(raw)));
  const i = s.search(new RegExp(`[;:()\\[\\]{}|<>]|\\s[${DASHES}]+\\s`));
  if (i >= 0) s = s.slice(0, i);
  s = cutAtSentence(s).replace(/[\u2010-\u2015\u2212-]/g, ' ').replace(/\s+/g, ' ');
  const parts = s.split(/\s*,\s*/).map(x => x.trim()).filter(Boolean);
  const kept = [];
  let street = false, place = false, country = false, skipped = 0;
  for (const part of parts) {
    const words = cleanWords(part);
    if (ADDRESS_PHRASE.test(part)) break;   // an assessment phrase ends the address at this part
    const whole = words.join(' ') === part;
    if (!place) {
      const st = streetOf(words);
      if (st) { kept.push(st); street = true; if (st !== part) break; continue; }
    }
    if (!street) { if (++skipped > 2) break; continue; }   // a building name before the street is left out
    if (!place && isBuildingPart(words) && ++skipped <= 3) continue;   // a building name after a flat or unit part is left out
    const pl = whole ? placeOf(words, place) : null;
    if (!pl || (pl.kind === 'country' && country)) break;   // at most one country
    kept.push(pl.text);
    if (pl.kind === 'place') place = true;
    if (pl.kind === 'country') country = true;
  }
  return street ? kept.join(', ').replace(/[\s.]+$/, '') : '';
}
const COUNTRIES = ['United Kingdom', 'England and Wales', 'England', 'Scotland', 'Wales', 'Northern Ireland', 'Ireland', 'Isle of Man', 'Jersey', 'Guernsey', 'Gibraltar',
  'Switzerland', 'Liechtenstein', 'Germany', 'Austria', 'France', 'Monaco', 'Netherlands', 'Belgium', 'Luxembourg', 'Italy', 'San Marino', 'Spain', 'Andorra', 'Portugal', 'Greece', 'Cyprus', 'Malta',
  'Denmark', 'Sweden', 'Norway', 'Finland', 'Iceland', 'Estonia', 'Latvia', 'Lithuania', 'Poland', 'Czech Republic', 'Czechia', 'Slovakia', 'Hungary', 'Romania', 'Bulgaria', 'Croatia', 'Slovenia', 'Serbia', 'Albania', 'North Macedonia', 'Montenegro', 'Bosnia and Herzegovina', 'Moldova', 'Ukraine', 'Belarus', 'Russia', 'Russian Federation', 'Georgia', 'Armenia', 'Azerbaijan', 'Turkey', 'Turkiye',
  'United States', 'United States of America', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Uruguay', 'Venezuela', 'Cuba', 'Panama', 'Costa Rica', 'Belize', 'Bahamas', 'Barbados', 'Bermuda', 'Cayman Islands', 'British Virgin Islands', 'Anguilla', 'Turks and Caicos Islands', 'Curacao', 'Aruba', 'Saint Kitts and Nevis', 'Saint Vincent and the Grenadines', 'Saint Lucia', 'Antigua and Barbuda', 'Dominica', 'Grenada', 'Jamaica', 'Trinidad and Tobago',
  'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Kuwait', 'Oman', 'Israel', 'Jordan', 'Lebanon', 'Egypt', 'Morocco', 'Tunisia', 'Algeria', 'Libya', 'Iran', 'Iraq', 'Syria', 'Yemen', 'Afghanistan', 'Pakistan', 'India', 'Bangladesh', 'Sri Lanka', 'Nepal',
  'China', 'Hong Kong', 'Macau', 'Taiwan', 'Japan', 'South Korea', 'North Korea', 'Mongolia', 'Kazakhstan', 'Uzbekistan', 'Kyrgyzstan', 'Singapore', 'Malaysia', 'Labuan', 'Indonesia', 'Thailand', 'Vietnam', 'Philippines', 'Myanmar', 'Cambodia',
  'Australia', 'New Zealand', 'Marshall Islands', 'Samoa', 'Vanuatu', 'South Africa', 'Nigeria', 'Ghana', 'Kenya', 'Mauritius', 'Seychelles', 'Rwanda', 'Uganda', 'Tanzania', 'Ethiopia', 'Botswana', 'Namibia', 'Zambia', 'Zimbabwe'];
const COUNTRY_ALIASES = { 'uk': 'United Kingdom', 'gb': 'United Kingdom', 'great britain': 'United Kingdom', 'britain': 'United Kingdom', 'bvi': 'British Virgin Islands', 'uae': 'United Arab Emirates', 'usa': 'United States', 'us': 'United States', 'republic of ireland': 'Ireland', 'swiss confederation': 'Switzerland', 'the netherlands': 'Netherlands', 'holland': 'Netherlands', 'st vincent and the grenadines': 'Saint Vincent and the Grenadines', 'st kitts and nevis': 'Saint Kitts and Nevis', 'korea': 'South Korea', 'republic of korea': 'South Korea' };
const COUNTRY_KEY = new Map([...COUNTRIES.map(c => [c.toLowerCase(), c]), ...Object.entries(COUNTRY_ALIASES)]);
export const COUNTRY_NAMES = Object.freeze([...COUNTRIES]);
/** A jurisdiction or country from the fixed list, in the list's own wording, or ''. */
export function allowedCountry(raw) {
  const s = squash(str(raw)).replace(/[.\s]+$/, '').toLowerCase().replace(/&/g, 'and').replace(/^the\s+/, '');
  return COUNTRY_KEY.get(s) || '';
}

// ── Fixed vocabularies: entity type, permissions, documents ─────────────────
const ENTITY_TYPES = [
  ['public limited company', /\bplc\b|public\s+limited/i],
  ['limited liability partnership', /\bLLP\b|limited\s+liability\s+partnership/i],
  ['limited liability company', /\bLLC\b|limited\s+liability\s+company/i],
  ['private limited company', /private\s+limited|limited\s+company|\bltd\b|\bGmbH\b|\bAG\b|\bS\.?A\.?R\.?L\b|\bB\.?V\b/i],
  ['partnership', /partnership/i],
  ['trust', /\btrust\b/i],
  ['foundation', /foundation|stiftung/i],
  ['sole trader', /sole\s+trader|sole\s+proprietor/i],
  ['charity', /charit/i],
  ['investment fund', /\bfund\b|\bSICAV\b|\bSPC\b|REGULATED_FUND/i],
  ['corporate entity', /\bcorporat/i],
];
export const ENTITY_TYPE_LABELS = Object.freeze(ENTITY_TYPES.map(([l]) => l));
/** The entity type as a fixed label, or ''. */
export function allowedEntityType(raw) {
  const s = squash(str(raw));
  const hit = s && ENTITY_TYPES.find(([, re]) => re.test(s));
  return hit ? hit[0] : '';
}
// The client's own intake answer (src/App.jsx DF_QUESTIONS, businessType), as
// the option the client chose, written without a slash or dash.
export const BUSINESS_TYPE_LABELS = Object.freeze({
  REGULATED_FUND: 'an investment fund', ASSET_MANAGER: 'an asset or investment manager', CORPORATE_TREASURY: 'an operating company or corporate treasury',
  BROKER: 'a broker or trading firm', VASP: 'a crypto or digital asset business', MSB: 'a payments or money services business', GAMING: 'an online gaming business',
  FAMILY_OFFICE: 'a family office', HOLDING_SPV: 'a holding company or SPV',
});
export const BUSINESS_LINE_LABEL = 'Business (as the client described it at the start):';
/** The business as the client described it at intake (collected_data.intake.businessType, or dfContext.statedBusinessType), or ''. */
export function clientBusinessLine(collected) {
  const cd = collected && typeof collected === 'object' ? collected : {};
  const intake = cd.intake && typeof cd.intake === 'object' ? cd.intake : {};
  const ctx = cd.dfContext && typeof cd.dfContext === 'object' ? cd.dfContext : {};
  const v = [intake.businessType, ctx.statedBusinessType].find(x => typeof x === 'string' && Object.prototype.hasOwnProperty.call(BUSINESS_TYPE_LABELS, x));
  return v ? `${BUSINESS_LINE_LABEL} ${BUSINESS_TYPE_LABELS[v]}` : '';
}

// Permissions: within a group the first match only (small e money before e money).
const PERMISSION_GROUPS = [
  [['small electronic money institution', /small\s+e(?:lectronic)?[\s-]?money|\bSEMI\b/i], ['electronic money institution', /\bE\.?M\.?I\b|e[\s-]?money|electronic\s+money/i]],
  [['small payment institution', /small\s+payment\s+institution|\bSPI\b/], ['authorised payment institution', /authori[sz]ed\s+payment\s+institution|\bAPI\b/], ['payment institution', /payment\s+institution|payment\s+services?\b/i]],
  [['investment firm', /investment\s+firm|\bMiFID\b/i]],
  [['credit institution', /credit\s+institution|banking\s+licen[cs]e|deposit[\s-]taking/i]],
  [['crypto asset business registration', /crypto[\s-]?assets?|virtual\s+assets?|\bVASP\b|\bCASP\b|\bMiCA\b/i]],
  [['money service business', /\bMSB\b|money\s+service|money\s+transmi|remittance/i]],
  [['self regulatory organisation membership', /\bSRO\b|self[\s-]regulat|\bVQF\b|PolyReg|financial\s+intermediar/i]],
  [['fund management', /\bAIFM\b|fund\s+manag|UCITS\s+management|\bManCo\b/i]],
  [['consumer credit', /consumer\s+credit/i]],
  [['gambling licence', /gambling|gaming\s+licen/i]],
  [['advising on investments', /advis(?:ing|e)\s+on\s+investments|investment\s+advi[cs]e/i]],
  // Each activity's own label reads back as itself ("mortgage advice or arranging").
  ...ACTIVITIES.filter(a => a.listed).map(a => [[a.label, new RegExp(a.listed.source + '|' + a.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')]]),
  ...ACTIVITIES.filter(a => a.deniedBy).map(a => [['may not hold or control client money', a.deniedBy]]),
];
export const PERMISSION_LABELS = Object.freeze(uniq(PERMISSION_GROUPS.flatMap(g => g.map(([l]) => l))));
/** The permissions on the regulator record as fixed labels (vocabulary order), never the board's wording. */
export function permissionLabels(raw) {
  const s = squash(str(raw));
  if (!s) return [];
  const out = [];
  for (const group of PERMISSION_GROUPS) { const hit = group.find(([, re]) => re.test(s)); if (hit) out.push(hit[0]); }
  return uniq(out);
}

// Documents: the fixed labels the owner listed (22/10/2026).
export const DOCUMENT_LABELS = Object.freeze([
  ['certificate of incorporation', /certificate\s+of\s+(?:incorporation|registration)|incorporation\s+certificate|registry\s+extract|commercial\s+register|handelsregister|trade\s+register|companies\s+house\s+(?:extract|filing|record)/i],
  ['register of directors', /register\s+of\s+directors|directors'?\s+register|list\s+of\s+directors|incumbency/i],
  ['register of members or shareholders', /register\s+of\s+(?:members|shareholders)|shareholders?'?\s+register|members'?\s+register|share\s+register|\bPSC\b|persons?\s+with\s+significant\s+control/i],
  ['ownership structure chart', /structure\s+chart|ownership\s+(?:chart|structure)|organi[sz]ational\s+chart|group\s+structure|group\s+chart/i],
  ['articles of association', /articles\s+of\s+association|memorandum\s+(?:and|of)|\barticles\b|\bstatutes\b|by[\s-]?laws|constitutional\s+documents?|operating\s+agreement/i],
  ['regulatory licence or authorisation', /(?<!driving\s)licen[cs]e(?!\s+number)|authori[sz]ation\s+(?:letter|certificate|notice)|registration\s+certificate|\bVASP\s+registration|regulatory\s+(?:permission|approval|status)/i],
  ['AML policy', /\bAML\b|anti[\s-]money\s+laundering|\bAML\/CTF\b|\bCTF\b|compliance\s+(?:policy|manual)|KYC\s+(?:policy|procedures?)/i],
  ['bank statement', /bank\s+statements?|statements?\s+of\s+account|account\s+statements?/i],
  ['proof of address', /proof\s+of\s+(?:residential\s+)?address|utility\s+bills?|address\s+(?:proof|verification\s+document)/i],
  ['passport or identity document', /passports?|identity\s+(?:documents?|cards?)|\bID\s+(?:cards?|documents?)|national\s+id|driving\s+licen[cs]e|photo\s+id/i],
  ['source of wealth evidence', /source\s+of\s+wealth|\bSoW\b/i],
  ['source of funds evidence', /source\s+of\s+funds|\bSoF\b/i],
  ['audited accounts', /audited|annual\s+(?:accounts|report|financial)|financial\s+statements|statutory\s+accounts|filed\s+accounts|annual\s+return/i],
  ['management accounts', /management\s+accounts/i],
  ['share purchase agreement', /share\s+(?:purchase|sale)\s+agreement|sale\s+and\s+purchase\s+agreement|\bSPA\b/i],
  ['loan agreement', /loan\s+(?:agreement|facility|contract|documents?)|facility\s+agreement/i],
  ['board resolution', /board\s+resolution|resolution\s+of\s+the\s+board|board\s+minutes|trading\s+mandate/i],
  ['trust deed', /trust\s+deed|deed\s+of\s+trust|settlement\s+deed/i],
  ['partnership agreement', /partnership\s+(?:agreement|deed)/i],
  ['power of attorney', /power\s+of\s+attorney|\bPOA\b/i],
  ['tax return', /tax\s+returns?|tax\s+assessment|\bSA302\b|tax\s+filing/i],
  ['payslip', /pay\s?slips?|\bP60\b|salary\s+slips?/i],
  ['sale agreement', /sale\s+(?:agreement|contract)|contract\s+of\s+sale|sales?\s+agreement|title\s+deeds?|completion\s+of\s+(?:the\s+)?sale/i],
  ['gift letter', /gift\s+(?:letter|deed|declaration)|letter\s+of\s+gift/i],
  ['completion statement', /completion\s+statement/i],
]);
export const OTHER_DOCUMENT = 'another document the firm will confirm';
/** Every fixed document label a text names (vocabulary order), never its wording. */
export function documentLabels(raw) {
  const s = squash(str(raw));
  if (!s) return [];
  const out = DOCUMENT_LABELS.filter(([, re]) => re.test(s)).map(([l]) => l);
  // A share purchase agreement is not also a plain sale agreement.
  return out.includes('share purchase agreement') ? out.filter(l => l !== 'sale agreement') : out;
}
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** The cut names on the people list that a text names (whole words, any case). */
export function namesIn(raw, names) {
  const s = squash(str(raw));
  if (!s) return [];
  return (names || []).filter(n => n && n !== PERSON_PLACEHOLDER && n.length >= 3 && new RegExp(`(?<![\\p{L}])${escapeRe(n)}(?![\\p{L}])`, 'iu').test(s));
}
const gapText = (g) => (typeof g === 'string' ? g : (g && typeof g === 'object' ? [g.documentType, g.description, g.gap].map(str).filter(Boolean).join(' ') : str(g)));
/**
 * Each outstanding (or received) item as fixed document labels: a person
 * specific item adds the cut name of a person on the people list. An item that
 * maps to no label is counted; with ownLine it adds OTHER_DOCUMENT once.
 */
export function allowedDocuments(list, names, { counts, ownLine = true } = {}) {
  const out = [];
  let unmapped = 0;
  for (const g of (Array.isArray(list) ? list : [])) {
    const t = gapText(g);
    const labels = documentLabels(t);
    if (!labels.length) { if (squash(t)) unmapped++; continue; }
    const who = namesIn(t, names);
    for (const l of labels) {
      if (who.length) for (const n of who) out.push(`${l} for ${n}`);
      else out.push(l);
    }
  }
  if (counts) counts.documents += unmapped;
  if (unmapped && ownLine) out.push(OTHER_DOCUMENT);
  return uniq(out).slice(0, 16);
}

// ── The facts: one allowlisted object for every surface ─────────────────────
/**
 * The board as Alex may read it. Every field is a structured fact passed
 * through its allowlist rule; nothing else from the board is carried. Logs the
 * counts withheld (where names the caller, for the log line).
 */
// A facts object read back (a prompt's data block passes through twice on some
// paths): mapped onto the board's own fields and run through the same rules, so
// reading it again changes nothing and never lets anything new through.
const FACT_KEYS = ['companyName', 'companyNumber', 'jurisdiction', 'registeredAddress', 'incorporated', 'entityType', 'regulated', 'regulator', 'licenceNumber', 'permissions', 'permissionsWithheld', 'documentsReceived', 'fundingDocumentsOnFile', 'stillOutstanding'];
// Second review N5: a board carries clientName or documentGaps; a facts object never does.
const isFactsShape = (o) => !('companyDetails' in o) && !('regulatoryStatus' in o) && !('clientName' in o) && !('documentGaps' in o) && FACT_KEYS.some(k => k in o);
const arr = (v) => (Array.isArray(v) ? v : []);
function boardOfFacts(f) {
  const out = {
    clientName: f.companyName,
    companyDetails: { registrationNumber: f.companyNumber, jurisdiction: f.jurisdiction, registeredAddress: f.registeredAddress, dateOfIncorporation: f.incorporated },
    clientType: f.entityType,
    regulatoryStatus: { regulated: f.regulated === true, regulator: f.regulator, licenceNumber: f.licenceNumber, licenceType: arr(f.permissions).filter(x => typeof x === 'string').join(', ') },
    documentsReceived: arr(f.documentsReceived), financialEvidence: arr(f.fundingDocumentsOnFile).map(d => ({ docType: d })), documentGaps: arr(f.stillOutstanding),
  };
  for (const k of PEOPLE_KEYS) if (Array.isArray(f[k])) out[k] = f[k];
  return out;
}
/** The board in its own shape: a facts object read back is mapped onto the board's fields. */
export function asBoard(board) {
  if (!board || typeof board !== 'object' || Array.isArray(board)) return board;
  return isFactsShape(board) ? boardOfFacts(board) : board;
}
export function boardFacts(board, { where = 'board', log = true, collected = null, tallyOut = null } = {}) {
  const b0 = board && typeof board === 'object' && !Array.isArray(board) ? board : {};
  const readBack = isFactsShape(b0);
  const b = readBack ? boardOfFacts(b0) : b0;
  const counts = tally();
  const corroborated = corroborationSet(b, collected);
  const cd = b.companyDetails && typeof b.companyDetails === 'object' ? b.companyDetails : {};
  const rs = b.regulatoryStatus && typeof b.regulatoryStatus === 'object' ? b.regulatoryStatus : {};
  const ep = b.entityProfile && typeof b.entityProfile === 'object' ? b.entityProfile : {};
  const take = (raw, rule, key) => { const v = rule(raw); if (!v && squash(str(raw))) counts[key]++; return v; };
  const people = {};
  for (const k of PEOPLE_KEYS) if (Array.isArray(b[k])) people[k] = b[k].filter(p => p != null && p !== '').map(p => allowedPerson(p, counts, { corroborated }));
  const names = peopleNames(b, collected);
  // A facts object read back already holds the trusted name; a board's name is
  // used only when it matches a typed or register name (trustedCompanyName).
  const company = readBack ? { name: cutCompanyName(b.clientName), from: 'board' } : trustedCompanyName(b, collected);
  const boardHadName = !!squash(str(b.clientName || cd.legalName));
  if (boardHadName && company.from !== 'board') counts.company++;
  const regulator = take(rs.regulator, allowedRegulator, 'regulator');
  const permissions = permissionLabels(rs.licenceType);
  if (!permissions.length && squash(str(rs.licenceType))) counts.permissions++;
  const facts = {
    companyName: company.name,
    companyNumber: take(cd.registrationNumber, allowedNumber, 'numbers'),
    jurisdiction: take(cd.jurisdiction, allowedCountry, 'jurisdiction'),
    registeredAddress: take(cd.registeredAddress, allowedAddress, 'address'),
    incorporated: take(cd.dateOfIncorporation, allowedDate, 'date'),
    entityType: take(b.clientType || ep.entityType || cd.legalForm, allowedEntityType, 'entityType'),
    regulated: rs.regulated === true,
    regulator,
    licenceNumber: take(rs.licenceNumber, allowedNumber, 'numbers'),
    permissions,
    permissionsWithheld: !permissions.length && !!squash(str(rs.licenceType)),
    ...people,
    documentsReceived: allowedDocuments(b.documentsReceived, names, { counts, ownLine: false }),
    fundingDocumentsOnFile: allowedDocuments((Array.isArray(b.financialEvidence) ? b.financialEvidence : []).map(f => (f && typeof f === 'object' ? f.docType : f)), [], { counts, ownLine: false }),
    stillOutstanding: allowedDocuments(b.documentGaps, names, { counts }),
  };
  if (log) logWithheld(where, counts);
  if (tallyOut && typeof tallyOut === 'object') Object.assign(tallyOut, counts, { companyFellBack: boardHadName && company.from !== 'board' });
  return facts;
}

// ── The points to cover (documentsFirstQuestions) ───────────────────────────
// The platform's own fixed questions (src/App.jsx DF_ACTIVITY_QUESTIONS, the
// questions df-process appends at a High tier, for a VQF firm and for tax), as
// written, each with the form Alex reads (no dash).
export const PLATFORM_QUESTIONS = Object.freeze([
  'What is the intended purpose of your relationship with us, what will the account or service be used for?',
  'What types of transactions do you expect, and roughly what annual volume and typical transaction size?',
  'Which currencies will you transact in?',
  'Which countries will funds typically come from (inbound), and which countries will they go to (outbound)?',
  'Who are your main customers or counterparties, what is the split between corporate and individual, and in which countries are they based?',
  'Source of funds, for the money that will actually flow through this relationship, where will those specific funds originate: which bank account(s) or sources will be used to fund the activity, and are those accounts held in the company\'s own name?',
  'One standard question we ask every client: can you confirm that the assets you will bring into this relationship are fully tax compliant in all relevant jurisdictions, that is, properly declared to the relevant tax authorities and not connected to any tax offence? We ask because your answer is recorded as a declaration on your file, and the file cannot be completed without it.',
  'For any higher-risk countries involved in your inbound or outbound flows, could you explain the business reason for those flows and who the counterparties are?',
  'Could you confirm the full ownership and control chain through to the individuals who ultimately own or control the business?',
  'Could you give us an approximate figure for the company\'s total assets and its total liabilities?',
  'Where do the assets used in this relationship mainly come from, for example savings, the company\'s own business operations, inheritance, or something else? A brief description is fine.',
  'Was this relationship introduced or referred to us by anyone, such as an agent, adviser or introducer? If so, please tell us who; if not, just say none.',
  'What is the purpose of your relationship with us, and what do you intend to use the service for?',
  'Looking ahead, roughly how many payments a month do you expect to make through the relationship, what total monthly value, and in which currencies?',
  'Could you explain, in your own words, how the business makes its money: its main products or services and its main sources of income?',
]);
const noDash = (t) => t.replace(/(\w)-(\w)/g, '$1 $2');
const PLATFORM_KEY = new Map(PLATFORM_QUESTIONS.map(q => [squash(q).toLowerCase(), noDash(q)]));
const PLATFORM_ANY = new Set(PLATFORM_QUESTIONS.flatMap(q => [squash(q).toLowerCase(), squash(noDash(q)).toLowerCase()]));
/** Whether a text is one of the platform's own fixed questions, as written or in the form Alex reads (no dash). */
export function isPlatformQuestion(text) {
  return PLATFORM_ANY.has(squash(str(text)).toLowerCase());
}
const P = Object.fromEntries(['purpose', 'volumes', 'currencies', 'countries', 'customers', 'sof', 'tax', 'flows', 'ownership', 'assets', 'origin', 'introducer', 'purpose2', 'forward', 'revenue'].map((k, i) => [k, noDash(PLATFORM_QUESTIONS[i])]));
// Topics a board written point may ask about, each answered by the platform's
// own fixed question (a fixed label, never the point's wording).
const TOPICS = [
  [/\bpurpose\b|\bintend(?:s|ed)?\s+to\s+use\b|\bintended\s+use\b|\bwhat\s+(?:will|would)\s+(?:the|this|your)\s+(?:account|service|relationship)\s+be\s+used\b/i, P.purpose],
  [/\bvolumes?\b|\bhow\s+many\s+(?:payments|transactions)\b|\btransaction\s+sizes?\b|\bmonthly\s+(?:value|turnover)\b/i, P.volumes],
  [/\bcurrenc(?:y|ies)\b/i, P.currencies],
  [/\b(?:inbound|outbound)\b|\bwhich\s+countries\b|\bcountries\s+(?:will|do|does)\b/i, P.countries],
  [/\bwho\s+are\s+(?:your|the)\s+(?:main\s+)?(?:customers|clients|counterpart|suppliers)|\b(?:customers|clients|counterparties)\b[^?]{0,60}\b(?:split|based|corporate\s+(?:and|or)\s+individual)\b/i, P.customers],
  [/\b(?:origin|source)s?\s+of\s+(?:the\s+|those\s+|these\s+|this\s+)?(?:funds|money|transfers?|payments?|deposits?)\b|\b(?:transfers?|payments?|deposits?|inflows?|remittances?)\s+(?:received\s+)?from\b|\bhow\s+will\s+(?:the\s+)?(?:account|relationship|activity)\s+be\s+funded\b|\bwhere\s+will\s+(?:the|those|these)\s+(?:specific\s+)?funds\b/i, P.sof],
  [/\b(?:revenue|business)\s+model\b|\bhow\s+(?:does\s+)?(?:the|your)\s+(?:business|company)\s+(?:makes?|earns?|generates?)\s+(?:its\s+)?(?:money|revenue|income)\b|\bsources?\s+of\s+(?:revenue|income)\b/i, P.revenue],
  [/\bownership\s+and\s+control\b|\bcontrol\s+chain\b|\bultimately\s+own\b|\bclass(?:es)?\s+of\s+shares\b|\bwho\s+(?:holds?|owns?|controls?)\b|\bconfirm\s+(?:who\s+has\s+)?control\b/i, P.ownership],
  [/\btax\s+complian/i, P.tax],
  [/\bintroduc(?:er|ed)\b|\breferred\s+to\s+us\b/i, P.introducer],
  [/\btotal\s+(?:assets|liabilities)\b/i, P.assets],
];
const EMAIL_LEAD = 'For identity verification, our compliance team will send a secure individual link to each of the following people:';
const EMAIL_TAIL = 'Could you give us the best direct email address for each of them? We ask because each link is personal to the individual and goes straight to them.';
const GAP_LEAD = /^You mentioned ([a-z ()]+?)\.\s/;
const EMAIL_ASK = /^Could you give us a direct email address for |\be-?mail\b[^?]{0,160}\b(?:identity|verif\w*)\b|\b(?:identity|verif\w*)\b[^?]{0,160}\be-?mail\b/i;
const joinAnd = (xs) => (xs.length <= 1 ? (xs[0] || '') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1]);
/**
 * One point to cover as Alex may read it, rebuilt from fixed text. Returns the
 * texts it becomes (empty when it maps to nothing). A source of wealth question
 * is the caller's (clientSowQuestions, the neutral own words question).
 * ctx: { names } the cut names on the people list, { board } for the people with no email.
 */
export function allowedPointTexts(text, ctx = {}) {
  const t = squash(str(text));
  if (!/\p{L}/u.test(t)) return [];
  const platform = PLATFORM_KEY.get(t.toLowerCase());
  if (platform) return [platform];
  const names = Array.isArray(ctx.names) ? ctx.names : peopleNames(ctx.board);
  // df-process's identity email point names the people; rebuilt with cut names.
  if (t.startsWith(EMAIL_LEAD)) {
    let who = [];
    const b = ctx.board && typeof ctx.board === 'object' ? ctx.board : null;
    // N7: a person whose name is not used is named by role ("the director named on the file").
    const corroborated = b ? corroborationSet(b, ctx.collected) : null;
    if (b) who = PEOPLE_KEYS.flatMap(k => (Array.isArray(b[k]) ? b[k] : []).map(p => [k, p])).filter(([, p]) => p && typeof p === 'object' && !EMAIL.test(squash(str(p.email)))).map(([k, p]) => { const n = allowedPerson(p, null, { corroborated }).name; return n === PERSON_PLACEHOLDER ? rolePlaceholder(k) : n; });
    if (!who.length) who = t.slice(EMAIL_LEAD.length).split(/\.\s+Could you/)[0].split(/\s*;\s*/).map(x => cutPersonName(x) || PERSON_PLACEHOLDER);
    const isRole = (n) => n === PERSON_PLACEHOLDER || / named on the file$/.test(n);
    who = [...uniq(who.filter(n => !isRole(n))), ...uniq(who.filter(isRole))].slice(0, 12);
    return who.length ? [`${EMAIL_LEAD} ${who.join('; ')}. ${EMAIL_TAIL}`] : [];
  }
  // df-process's permission gap point (permissionGapQuestions): the activity
  // label is fixed; the record's wording is not carried.
  const gm = t.match(GAP_LEAD);
  const act = gm && ACTIVITIES.find(a => a.label === gm[1]);
  if (act) return [`You mentioned ${act.label}. The register record we hold for the firm does not show a permission for it. Could you tell us how that activity is carried on, for example under another firm's permission, as an appointed representative, under a pending application, or not at present?`];
  // An identity email ask: the cut names of the people on the list it names,
  // else every director and beneficial owner, in fixed words.
  if (EMAIL_ASK.test(t)) {
    const who = namesIn(t, names);
    return [`Could you give us a direct email address for ${who.length ? joinAnd(who.slice(0, 8)) : 'each director and beneficial owner'}, so our compliance team can send each person their own identity verification link?`];
  }
  // A point that names a document becomes a request for it only when the
  // point is itself a request for that document (provide, upload, send, share,
  // a copy of, or the point is the document's own name, as a gap line) and the
  // document is not already on file. Otherwise it is read for its topic ("the
  // transfer shown on the March bank statement" asks about the funds, not for
  // the statement); a point with neither is left out (counted by the caller).
  const labels = requestedLabels(t);
  if (labels.length) {
    const who = namesIn(t, names);
    const onFile = documentsOnFile(ctx);
    const items = labels.flatMap(l => (who.length ? who.map(n => [`${l} for ${n}`, l]) : [[l, l]]))
      .filter(([key, l]) => !onFile.has(key) && !(key === l && onFile.has(l)))
      .map(([key]) => `the ${key}`);
    if (items.length) return [`Could you provide ${joinAnd(uniq(items).slice(0, 6))}?`];
  }
  return uniq(TOPICS.filter(([re]) => re.test(t)).map(([, q]) => q));
}
// The verbs that make a point a request for the document that follows them.
const REQUEST_LEAD = /(?:\b(?:provide|upload|send|share|submit|supply|forward|attach|obtain|request|need|needs|needed|require|requires|required|missing|outstanding|copy|copies|ask\s+for|asking\s+for|let\s+us\s+have|email|e-mail)\b)(?:\s+(?:us|me|a|an|the|your|our|its|their|his|her|this|that|these|those|certified|signed|dated|latest|recent|most|current|full|complete|original|scanned|clear|legible|updated|final|executed|copy|copies|of|for|from|with|over|in|last|past|six|three|twelve|\d+|months?|years?|the\s+company's|company|\w+'s))*\s*$/i;
// A gap line is the document's own name ("Passport for Helen Fraud", "Bank
// statements for the last six months"): the label opens the point, and its first
// clause asks nothing else.
const ASK_ELSE = /\b(?:explain|clarify|describe|tell|confirm|why|who|whom|how|what|which|when|where|name|names|named|show|shows|showing|shown|state|states|list|lists|indicate|indicates|mention|mentions|refer|refers|appear|appears|differ|differs|hold|holds|contain|contains|reveal|reveals|suggest|suggests|is|are|was|were)\b/i;
const LEAD_ADJ = /^(?:(?:a|an|certified|signed|dated|latest|recent|current|full|complete|original|scanned|updated|final|executed|copy|copies|of|your|company|the\s+company's)\s+)*/i;
/** The fixed document labels a point asks FOR (a request for the document itself), in vocabulary order. */
export function requestedLabels(raw) {
  const t = squash(str(raw));
  if (!t) return [];
  const firstClause = t.split(/[;:?!]|\.(?=\s|$)|,\s/)[0];
  const out = [];
  for (const [l, re] of DOCUMENT_LABELS) {
    const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
    let m, hit = false;
    while (!hit && (m = g.exec(t))) {
      const before = t.slice(0, m.index);
      const clause = before.split(/[.;?!]\s/).pop();
      if (REQUEST_LEAD.test(clause)) hit = true;
      else if (!before.replace(LEAD_ADJ, '').trim() && !ASK_ELSE.test(firstClause)) hit = true;
      if (m[0] === '') g.lastIndex++;
    }
    if (hit) out.push(l);
  }
  return out.includes('share purchase agreement') ? out.filter(x => x !== 'sale agreement') : out;
}
/**
 * The documents already on file, as fixed labels (person specific where the
 * item names a person): documentsReceived and the funding evidence (or
 * ctx.received), less any the board still lists as outstanding (a signed or
 * newer copy the documentGaps still ask for keeps its request).
 */
function documentsOnFile(ctx) {
  if (Array.isArray(ctx.received)) return new Set(ctx.received);
  const b = ctx.board && typeof ctx.board === 'object' && !Array.isArray(ctx.board) ? ctx.board : null;
  if (!b) return new Set();
  const names = Array.isArray(ctx.names) ? ctx.names : peopleNames(b);
  // The board's own fields, or a facts object's (fundingDocumentsOnFile, stillOutstanding).
  const funding = Array.isArray(b.financialEvidence) ? b.financialEvidence.map(f => (f && typeof f === 'object' ? f.docType : f)) : arr(b.fundingDocumentsOnFile);
  const got = [
    ...allowedDocuments(b.documentsReceived, names, { ownLine: false }),
    ...allowedDocuments(funding, [], { ownLine: false }),
  ];
  const still = new Set(allowedDocuments(Array.isArray(b.documentGaps) ? b.documentGaps : arr(b.stillOutstanding), names, { ownLine: false }));
  return new Set(got.filter(d => !still.has(d) && !still.has(d.split(' for ')[0])));
}
